import { useState, useCallback, useEffect } from "react";

const ENABLED_KEY = "tpt_tutorial_enabled";
const DISMISSED_KEY = "tpt_tutorial_dismissed";
const GLOBALLY_OFF_KEY = "tpt_tips_globally_off";
const SYNC_EVENT = "tpt:tutorial-change";

function getEnabled(): boolean {
  const val = localStorage.getItem(ENABLED_KEY);
  return val === null ? true : val !== "false";
}

function getGloballyOff(): boolean {
  const val = localStorage.getItem(GLOBALLY_OFF_KEY);
  return val === null ? false : val === "true";
}

function getDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
}

function dispatch() {
  window.dispatchEvent(new Event(SYNC_EVENT));
}

export function useTutorial() {
  const [enabled, setEnabledState] = useState<boolean>(getEnabled);
  const [globallyOff, setGloballyOffState] = useState<boolean>(getGloballyOff);
  const [dismissed, setDismissedState] = useState<string[]>(getDismissed);

  useEffect(() => {
    const handler = () => {
      setEnabledState(getEnabled());
      setGloballyOffState(getGloballyOff());
      setDismissedState(getDismissed());
    };
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  const isTipVisible = useCallback(
    (id: string) => !globallyOff && !dismissed.includes(id),
    [globallyOff, dismissed]
  );

  const isTipDismissed = useCallback(
    (id: string) => dismissed.includes(id),
    [dismissed]
  );

  const dismissTip = useCallback((id: string) => {
    setDismissedState((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
      return next;
    });
    dispatch();
  }, []);

  const enableTour = useCallback(() => {
    localStorage.setItem(ENABLED_KEY, "true");
    setEnabledState(true);
    dispatch();
  }, []);

  const disableTour = useCallback(() => {
    localStorage.setItem(ENABLED_KEY, "false");
    setEnabledState(false);
    dispatch();
  }, []);

  const enableTips = useCallback(() => {
    localStorage.setItem(GLOBALLY_OFF_KEY, "false");
    localStorage.setItem(DISMISSED_KEY, "[]");
    setGloballyOffState(false);
    setDismissedState([]);
    dispatch();
  }, []);

  const disableTips = useCallback(() => {
    localStorage.setItem(GLOBALLY_OFF_KEY, "true");
    setGloballyOffState(true);
    dispatch();
  }, []);

  const enableTutorial = useCallback(() => {
    enableTour();
    enableTips();
  }, [enableTour, enableTips]);

  const disableTutorial = disableTour;
  const turnOffTips = disableTips;

  return {
    enabled,
    globallyOff,
    isTipVisible,
    isTipDismissed,
    dismissTip,
    enableTour,
    disableTour,
    enableTips,
    disableTips,
    enableTutorial,
    disableTutorial,
    turnOffTips,
  };
}
